import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { VcuDevice, sanitizeSerialNumber } from 'src/app/model/vcu-pair.model';
import { enviroment } from 'src/environments/environment';

@Component({
  selector: 'app-vcu-card-pair',
  templateUrl: './vcu-card-pair.component.html',
  styleUrls: ['./vcu-card-pair.component.css']
})
export class VcuCardPairComponent implements OnInit {
  @Input() vcu!: VcuDevice;
  @Output() editNickname = new EventEmitter<VcuDevice>();
  @Output() unpairVcu = new EventEmitter<VcuDevice>();

  isEditingNickname: boolean = false;
  editNicknameValue: string = '';
  vcuImageUrl: string = '';

  constructor(private router: Router, private translocoService: TranslocoService) {}

  ngOnInit(): void {
    this.loadVcuImage();
  }

  /** Serial saneado, ou texto amigável quando vier vazio/corrompido. */
  get displaySerial(): string {
    return sanitizeSerialNumber(this.vcu.serialNumber)
      ?? this.translocoService.translate('vcuPair.card.noSerial');
  }

  get displayName(): string {
    return this.vcu.nickname?.trim() || this.displaySerial;
  }

  get hasNickname(): boolean {
    return !!this.vcu.nickname?.trim();
  }

  get isActive(): boolean {
    return this.vcu.status === 'AC';
  }

  get statusClass(): string {
    return this.isActive ? 'status-green' : 'status-red';
  }

  get statusDescription(): string {
    return this.isActive ? 'vcuPair.card.active' : 'vcuPair.card.inactive';
  }

  get modelDisplay(): string {
    return this.vcu.modelDescription || this.vcu.vehicleModel || '-';
  }

  get lastAccessRelative(): string {
    if (!this.vcu.lastAccess) return '-';

    const now = new Date();
    const lastAccess = new Date(this.vcu.lastAccess);
    const diffMs = now.getTime() - lastAccess.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMinutes < 1) return 'agora';
    if (diffMinutes < 60) return `há ${diffMinutes} min`;
    if (diffHours < 24) return `há ${diffHours}h`;
    if (diffDays < 30) return `há ${diffDays}d`;
    return lastAccess.toLocaleDateString();
  }

  get createdAtFormatted(): string {
    const date = new Date(this.vcu.createdAt);
    return date.toLocaleDateString();
  }

  private loadVcuImage(): void {
    // Usa vehicleModel ou modelDescription para montar a URL da imagem
    const modelName = this.vcu.vehicleModel || this.vcu.modelDescription || 'default';
    const imgName = modelName.replace(/\s+/g, '').toUpperCase();
    this.vcuImageUrl = `${enviroment.BASE_URL_API_IMAGE_VCU}/${imgName}.png`;
  }

  onImageError(): void {
    this.vcuImageUrl = '';
  }

  formatImage(): string {
    if (this.vcuImageUrl) {
      return `url(${this.vcuImageUrl})`;
    }
    return '';
  }

  startEditNickname(): void {
    this.editNicknameValue = this.vcu.nickname?.trim() || '';
    this.isEditingNickname = true;
  }

  cancelEditNickname(): void {
    this.isEditingNickname = false;
    this.editNicknameValue = '';
  }

  saveNickname(): void {
    if (this.editNicknameValue.trim()) {
      this.editNickname.emit({ ...this.vcu, nickname: this.editNicknameValue.trim() });
    }
    this.isEditingNickname = false;
  }

  onNicknameKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.saveNickname();
    }
    if (event.key === 'Escape') {
      this.cancelEditNickname();
    }
  }

  onUnpair(): void {
    this.unpairVcu.emit(this.vcu);
  }

  goVcuDetail(): void {
    this.router.navigate(['/vcu/', this.vcu.id]);
  }
}
